/**
 * Marks on the tech's job cards (The Quiet Toolbar, 10/10). The bell comes off
 * the iPad, so what it used to say about a job sits on the job's card instead
 * — each read off the job's own state (lib/job-marks.ts), so doing the work
 * clears it:
 *   red   "Office flagged this report"  (report-input queue: tester_todo)
 *   amber "Water off · waiting on NTE"  (fail protocol paused)
 *   amber "Check helper results"        (helper-entered tests not reviewed; lead only)
 *   amber "Parts not in yet"            (an open PO on the job)
 *   green "Clear to repair"             (fail protocol NTE approved, repair not done)
 * With the switch off, only "No work order yet" (2026-10-21) and "Collect
 * payment before you leave" (card hold #2037) show: a tech
 * who heads out without the work order has nothing to sign.
 */
import { useQuery } from "@tanstack/react-query";
import { Mark } from "@/components/mark";
import { collectOnSiteMark, jobMarks, workOrderMissingMark, type JobMarkFacts } from "@/lib/job-marks";
import { useQuietToolbar } from "@/lib/quiet-toolbar";
import { getTesterQueue } from "@/lib/report-input";

export function FieldJobMarks({ job, meId }: { job: JobMarkFacts & { id: string }; meId?: string | null }) {
    const [quiet] = useQuietToolbar();
    // Same query key the home screen already uses, so this is a cache read.
    const queue = useQuery({ queryKey: ["report-input", "queue"], queryFn: getTesterQueue, enabled: quiet });
    const always = [workOrderMissingMark(job, "tech"), collectOnSiteMark(job)].filter((m) => m !== null);
    if (!quiet && always.length === 0) return null;
    const reportFlagged = (queue.data?.items ?? []).some((i) => i.jobId === job.id);
    const marks = quiet ? jobMarks(job, { audience: "tech", meId, reportFlagged }) : always;
    if (marks.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1" data-testid="field-job-marks">
            {marks.map((m) => (
                <Mark key={m.label} kind={m.kind} label={m.label} title={m.title} />
            ))}
        </div>
    );
}
